import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Container,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  Button,
  Card,
} from "reactstrap";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import { publisherUserFormFields } from "variables/formFields";
import { DEFAULT_STATUS_LABELS } from "variables/constants";
import { showAlert } from "components/Common/commonFunctions";
import moment from "moment";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import NotificationAlert from "react-notification-alert";
import { STATUS_OPTIONS } from "variables/constants";
import { updateUser } from "actions/users";
import { userresetPassword } from "actions/users";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getPublisherList } from "actions/publisher";
import { createPublisherUser } from "actions/users";
import { getPublisherUserList } from "actions/users";
import { getPublisherUserDetails } from "actions/users";

const sortFieldMap = {
  publisher: "PublisherName",
  name: "Name",
  email: "Email",
  invitedOn: "CreatedAt",
  status: "Status",
};

const toNumber = (val) => (val !== undefined && val !== null ? Number(val) : 0);

function Publisher_UserList() {
  const [data, setData] = useState({ data: [], totalRecords: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [formState, setFormState] = useState({
    values: { status: true },
    errors: {},
  });
  const [dropDownData, setDropDownData] = useState({ publisherData: [] });
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    date_range: "",
    start_date: "",
    end_date: "",
    status: "",
    publisherID: "",
    filterParams: [],
  });

  const isEditMode = !!selected;

  // Fetch publisher users list
  const getPublisherUsersList = useCallback(async () => {
    try {
      setShowLoader(true);
      const res = await getPublisherUserList(
        pageNum,
        filters.filterParams,
        "",
        sortConfig.sortField,
        sortConfig.sortOrder,
      );
      if (res.code === 200) {
        setData({
          data: res?.data?.User || [],
          totalRecords: res?.data?.totalRecords || 0,
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShowLoader(false);
    }
  }, [pageNum, filters.filterParams, sortConfig.sortField, sortConfig.sortOrder]);

  // Fetch publishers for dropdown (only on mount)
  useEffect(() => {
    const fetchPublishers = async () => {
      try {
        const publisherRes = await getPublisherList(0);
        const publisherData =
          publisherRes?.code === 200
            ? (publisherRes?.data?.Publisher || []).map((item) => ({
                label: item?.companyName || "",
                value: item?.ID,
              }))
            : [];
        setDropDownData({ publisherData });
      } catch (error) {
        console.error("Error fetching publishers:", error);
      }
    };
    fetchPublishers();
  }, []);

  // Separate useEffect for fetching user list when dependencies change
  useEffect(() => {
    getPublisherUsersList();
  }, [getPublisherUsersList]);

  const notify = (type, message) => {
    notificationAlertRef.current?.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const handleChange = (e) => {
    const name = e?.target?.name || e?.name;
    const value = e?.target?.value ?? e?.value;
    let error = "";
    if (name === "email" && value) {
      error = validators.email(value);
    }
    setFormState((prev) => ({
      values: { ...prev.values, [name]: value },
      errors: { ...prev.errors, [name]: error },
    }));
  };

  const validators = {
    email: (value) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Enter a valid email address",
  };

  const validateForm = () => {
    let newErrors = {};
    publisherUserFormFields(dropDownData?.publisherData, isEditMode).forEach((field) => {
      const value = formState.values[field.name];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (field.required && isEmpty) {
        newErrors[field.name] = `${field.label} is required`;
        return;
      }
      if (field.validate?.pattern && value && !Array.isArray(value)) {
        if (!field.validate.pattern.test(value)) {
          newErrors[field.name] = field.validate.message;
        }
      }
    });
    setFormState((prev) => ({ ...prev, errors: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Create / Update user
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowLoader(true);
    try {
      const payload = {
        name: formState.values.name,
        email: formState.values.email,
        publisherID: toNumber(formState.values.publisherID),
        status: formState.values.status ? "ACTIVE" : "INACTIVE",
      };
      const res = !isEditMode
        ? await createPublisherUser(payload)
        : await updateUser(selected?.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getPublisherUsersList();
        setModalOpen(false);
        setSelected(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong");
    } finally {
      setShowLoader(false);
    }
  };

  // Toggle user status
  const confirmStatus = async (row) => {
    try {
      const newStatus = row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const payload = {
        name: row?.name,
        email: row?.email,
        publisherID: row?.publisherResponse?.publisherID,
        status: newStatus,
      };
      const res = await updateUser(row?.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getPublisherUsersList();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  // Get user details for edit (calls the detailed API)
  const getUserDetailsById = async (row) => {
    try {
      const res = await getPublisherUserDetails(row?.ID);
      if (res.code === 200) {
        const userData = res?.data;
        setSelected(userData);
        setFormState({
          values: {
            name: userData?.name || "",
            email: userData?.email || "",
            publisherID: userData?.publisherResponse?.publisherID || "",
            status: userData?.status === "ACTIVE",
          },
          errors: {},
        });
        setModalOpen(true);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to fetch user details");
    }
  };

  const handleEdit = (row) => {
    getUserDetailsById(row);
  };

  // Reset modal state when closed
  useEffect(() => {
    if (!modalOpen) {
      setSelected(null);
      setFormState({ values: { status: true }, errors: {} });
    }
  }, [modalOpen]);

  const handleStatusToggle = async (row) => {
    const isActive = row.status === "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${isActive ? "inactivate" : "activate"} this User?`,
      message: `User "${row.name}" will be ${isActive ? "inactive" : "active"}.`,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => confirmStatus(row),
      onCancel: () => {},
    });
  };

  const resetPassword = async (data) => {
    try {
      const res = await userresetPassword(data?.ID);
      if (res.code === 200) {
        notify("success", res?.message || "Mail Sent on your email");
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message);
    }
  };

  const handleResetPassword = (row) => {
    showAlert({
      title: "Reset Password?",
      message: `Password for "${row.name}" will be reset.`,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Reset",
      cancelText: "Cancel",
      onConfirm: () => resetPassword(row),
    });
  };

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    setPageNum(1); // Reset to first page when sorting changes
  };

  const columns = [
    {
      dataField: "publisher",
      text: "Publisher",
      formatter: (_, row) => row?.publisherResponse?.publisherName || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "name",
      text: "Name",
      formatter: (_, row) => row?.name || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "email",
      text: "Email",
      formatter: (_, row) => row?.email || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "invitedOn",
      text: "Invited On",
      formatter: (_, row) => row?.createdAt || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => (
        <Badge color={row.status === "ACTIVE" ? "success" : "danger"}>
          {row.status}
        </Badge>
      ),
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => {
        const rowId = row.ID;
        return (
          <>
            <a
              id={`edit${rowId}`}
              className="table-action table-action-info"
              onClick={() => handleEdit(row)}
            >
              <i className="fas fa-edit" />
            </a>
            <UncontrolledTooltip target={`edit${rowId}`}>Edit</UncontrolledTooltip>
            <a
              className="table-action table-action-delete"
              id={`reset${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                handleResetPassword(row);
              }}
            >
              <i className="fa-solid fa-key" />
            </a>
            <UncontrolledTooltip target={`reset${rowId}`}>Reset Password</UncontrolledTooltip>
            <a
              className={`table-action table-action-delete ${row.status === "ACTIVE" ? "status-active" : "status-inactive"}`}
              id={`toggle${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusToggle(row);
              }}
            >
              {row.status === "ACTIVE" ? <i className="fa-solid fa-xmark" /> : <i className="fa-solid fa-check" />}
            </a>
            <UncontrolledTooltip target={`toggle${rowId}`}>
              {row.status === "ACTIVE" ? "Mark Inactive" : "Mark Active"}
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

  // Filter helpers
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");
    if (value === "Custom Range") {
      setFilters((prev) => ({ ...prev, date_range: value, start_date: "", end_date: "" }));
      return;
    }
    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;
    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");
    setFilters((prev) => ({ ...prev, date_range: value, start_date: startDate, end_date: today }));
  };

  const buildFilterParams = () => {
    const filterParams = [];
    if (filters?.status) filterParams.push({ field: "Status", condition: "eq", filterValues: [filters.status] });
    if (filters?.publisherID) filterParams.push({ field: "PublisherID", condition: "eq", filterValues: [String(filters.publisherID)] });
    if (filters?.name) filterParams.push({ field: "Name", condition: "like", filterValues: [filters.name] });
    if (filters?.email) filterParams.push({ field: "Email", condition: "like", filterValues: [filters.email] });
    if (filters?.start_date && filters?.end_date) filterParams.push({ field: "CreatedAt", condition: "btw", filterValues: [filters.start_date, filters.end_date] });
    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;
        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return { ...prev, start_date: startDate };
        }
        return { ...prev, start_date: startDate, end_date: "" };
      }
      return { ...prev, [key]: value };
    });
  };

  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams" || key === "date_range") return false;
      return value !== "" && value !== null && value !== undefined;
    });
  };

  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) return;
    const newFilterParams = buildFilterParams();
    setFilters((prev) => ({ ...prev, filterParams: newFilterParams }));
    if (pageNum !== 1) setPageNum(1);
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) return;
    setFilters({
      name: "",
      email: "",
      date_range: "",
      start_date: "",
      end_date: "",
      status: "",
      publisherID: "",
      filterParams: [],
    });
  };

  const filterSection = () => (
    <>
      <Row className="align-items-center">
        <Col md={2}>
          <SearchableDropdown
            name="publisherID"
            options={dropDownData?.publisherData}
            placeholder="Select Publisher"
            value={filters.publisherID}
            onChange={(e) => handleFilterChange("publisherID", e.target.value)}
          />
        </Col>
        <Col md={2}>
          <input className="form-control" placeholder="Name" value={filters.name} onChange={(e) => handleFilterChange("name", e.target.value)} />
        </Col>
        <Col md={2}>
          <input className="form-control" placeholder="Email" value={filters.email} onChange={(e) => handleFilterChange("email", e.target.value)} />
        </Col>
        <Col md={2}>
          <SearchableDropdown options={STATUS_OPTIONS} placeholder="Status" value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)} isSearchable={false} />
        </Col>
      </Row>
      <Row className="mt-2">
        <Col md={2}>
          <SearchableDropdown options={DATE_RANGE_OPTIONS} placeholder="Date Range" value={filters.date_range} onChange={(e) => showCustomDateInputs("date_range", e.target.value)} isSearchable={false} />
        </Col>
        <Col md={2}>
          <div className="datepicker-wrapper">
            <DatePicker selected={filters.start_date ? new Date(filters.start_date) : null} onChange={(date) => handleFilterChange("start_date", date ? formatDate(date) : "")} dateFormat="dd MMM yyyy" placeholderText="Start Date" className="form-control custom-datepicker" disabled={filters.date_range !== "Custom Range"} />
            <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
          </div>
        </Col>
        <Col md={2}>
          <div className="datepicker-wrapper">
            <DatePicker selected={filters.end_date ? new Date(filters.end_date) : null} onChange={(date) => handleFilterChange("end_date", date ? formatDate(date) : "")} dateFormat="dd MMM yyyy" placeholderText="End Date" className="form-control custom-datepicker" disabled={filters.date_range !== "Custom Range" || !filters.start_date} minDate={filters.start_date ? new Date(filters.start_date) : undefined} />
            <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
          </div>
        </Col>
        <Col md={4}>
          <Button onClick={handleSearch} disabled={!isAnyFilterApplied(filters)} className="filter-btn-gradient-primary btn-md responsive">Search</Button>
          <Button className="secondary" disabled={!isAnyFilterApplied(filters)} onClick={handleReset}>Reset</Button>
        </Col>
      </Row>
    </>
  );

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="Publisher Users"
            description="Manage Publisher Users"
            data={data?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Add Publisher User"
            onAddBtnClick={() => {
              setSelected(null);
              setFormState({ values: { status: true }, errors: {} });
              setModalOpen(true);
            }}
            renderFilters={filterSection}
            page={pageNum}
            sizePerPage={10}
            totalRecords={data?.totalRecords}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        </Card>
      </Container>
      <DynamicFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isEditMode ? "Edit User" : "Add User"}
        fields={publisherUserFormFields(dropDownData?.publisherData, isEditMode)}
        formData={formState.values}
        errors={formState.errors}
        onChange={handleChange}
        onSubmit={handleUserSubmit}
        formState={formState}
        setFormState={setFormState}
        isEditMode={isEditMode}
        trueLabel={DEFAULT_STATUS_LABELS.active}
        falseLabel={DEFAULT_STATUS_LABELS.inactive}
        showLoader={showLoader}
      />
    </>
  );
}

export default Publisher_UserList;